"use client"

/* ============================================================================
   Switch + RadioPills — los dos controles de selección de tadmin
   ---------------------------------------------------------------------------
   FUENTE DE VERDAD: `components/YoolToggle/YoolToggle.jsx` del Yool DS
   (proyecto de Claude Design 1a4bd3, archivo "YoolToggle (standalone).html").
   Las medidas, colores y transiciones de abajo son las del DS copiadas tal
   cual, no una interpretación:

     switch  42×24, radio 9999, thumb 20px en top/left 2 → translateX(18px)
             OFF #E0E0E0 · ON #1A1A1A (Negro Apple) · transición .18s ease
     pill    alto 38, padding 0 16, borde 1.5px, radio 9999, 13px/600
             reposo #FFF sobre borde #E0E0E0 con texto #54585A
             activo relleno #1A1A1A con texto blanco · transición .15s ease
             punto FancyYool de 8px que CONSERVA su color sobre el pill negro

   POR QUÉ EL ON ES NEGRO Y NO TEAL: el teal (#007D6E) queda reservado al único
   CTA primario de cada vista — es la regla escrita en styles/ds/colors_and_type
   .css:103. Hasta esta versión este archivo pintaba el switch encendido en
   teal, así que catorce pantallas competían con su propio botón de acción.

   POR QUÉ YA NO USA RADIX: el DS es un `<button role="switch">` plano. Radix
   traía su propio DOM (root + thumb + input espejo) y obligaba a reproducir el
   diseño con estilos inline, que es exactamente de donde venía la deriva. Se
   conserva lo que Radix daba y aquí se usaba: `name` (input espejo oculto),
   `id` (un <button> es labelable, así que `<Label htmlFor>` sigue funcionando)
   y el control opcionalmente no controlado vía `defaultChecked`.

   CUANDO EL BUNDLE DEL DS ESTÁ CARGADO no se delega en él a propósito: su
   `YoolToggle` no conoce `size="sm"` ni las etiquetas/hint/error que usan las
   pantallas de tadmin, y mezclar las dos implementaciones deja dos switches
   distintos en la misma pantalla. Se replica el CSS del DS, byte a byte, bajo
   clases propias (`ysw-`) para no pisar el `#y-sel-css` que inyecta el bundle.

   @see apps-design-system/project/components/YoolToggle/YoolToggle.jsx
   @see docs/design-system-unico.md
   ========================================================================== */

import * as React from "react"
import { cn } from "src/lib/utils"

/* ── CSS del DS ───────────────────────────────────────────────────────────────
   Se inyecta una sola vez. Las reglas de `.ysw-switch` y `.ysw-pill` son las de
   `.y-switch` y `.y-radio__pill` del DS; lo añadido está marcado. */
const CSS = `
.ysw-switch{appearance:none;-webkit-appearance:none;position:relative;width:42px;height:24px;border-radius:9999px;
  background:var(--yool-border,#E0E0E0);border:0;cursor:pointer;flex-shrink:0;transition:background .18s ease;outline:none;}
.ysw-switch::after{content:"";position:absolute;top:2px;left:2px;width:20px;height:20px;border-radius:50%;
  background:#fff;box-shadow:0 1px 2px rgba(0,0,0,.2);transition:transform .18s ease;}
.ysw-switch[aria-checked="true"]{background:var(--yool-black,#1A1A1A);}
.ysw-switch[aria-checked="true"]::after{transform:translateX(18px);}
.ysw-switch:focus-visible{box-shadow:0 0 0 3px rgba(0,125,110,.25);}
.ysw-switch:disabled{opacity:.45;cursor:not-allowed;}

/* Extensión de tadmin: talla chica para el switch dentro de una fila de tabla
   (product_config/GroupedTable). Misma proporción, 36×20 con thumb de 16. */
.ysw-switch--sm{width:36px;height:20px;}
.ysw-switch--sm::after{width:16px;height:16px;}
.ysw-switch--sm[aria-checked="true"]::after{transform:translateX(16px);}

.ysw-radio{display:inline-flex;flex-wrap:wrap;gap:8px;}
.ysw-pill{display:inline-flex;align-items:center;gap:8px;font-family:var(--font-sans);font-size:13px;font-weight:600;height:38px;padding:0 16px;border-radius:9999px;
  border:1.5px solid var(--yool-border,#E0E0E0);background:#fff;color:var(--yool-gray1,#54585A);cursor:pointer;
  transition:border-color .15s ease,color .15s ease,background .15s ease;white-space:nowrap;}
.ysw-pill:hover{border-color:var(--yool-muted,#8D8D8D);color:var(--yool-black,#1A1A1A);}
.ysw-pill[aria-checked="true"]{border-color:var(--yool-black,#1A1A1A);background:var(--yool-black,#1A1A1A);color:#fff;}
.ysw-pill:disabled{opacity:.45;cursor:not-allowed;}
.ysw-pill__dot{width:8px;height:8px;border-radius:50%;flex-shrink:0;}
/* Añadido a lo del DS: el pill no traía foco visible y se navega con teclado. */
.ysw-pill:focus-visible{outline:none;box-shadow:0 0 0 3px rgba(0,125,110,.25);}
`

let cssPuesto = false
function ponerEstilos() {
  if (cssPuesto || typeof document === "undefined") return
  if (document.getElementById("ysw-css")) { cssPuesto = true; return }
  const s = document.createElement("style")
  s.id = "ysw-css"
  s.textContent = CSS
  document.head.appendChild(s)
  cssPuesto = true
}

/* ── Estado controlado o no ────────────────────────────────────────────────
   `checked` presente manda; si no, el componente recuerda por su cuenta desde
   `defaultChecked`. Es la semántica que traía Radix y de la que dependen las
   stories y los formularios de admin. */
function useCheck(checked, defaultChecked, onCheckedChange) {
  const controlado = checked !== undefined
  const [interno, setInterno] = React.useState(defaultChecked ?? false)
  const valor = controlado ? checked : interno
  const cambiar = React.useCallback((next) => {
    if (!controlado) setInterno(next)
    if (onCheckedChange) onCheckedChange(next)
  }, [controlado, onCheckedChange])
  return [Boolean(valor), cambiar]
}

/* ── Switch ─────────────────────────────────────────────────────────────────
   `label`/`hint`/`errorMessage` son de tadmin, no del DS: varias pantallas de
   admin muestran el texto al lado del control. La sangría del hint es el ancho
   del track más el gap (42+12), para que el texto arranque bajo la etiqueta. */
const Switch = React.forwardRef(({
  className,
  label,
  hint,
  error,
  errorMessage,
  size = "md",
  disabled,
  checked,
  defaultChecked,
  onCheckedChange,
  name,
  ...props
}, ref) => {
  ponerEstilos()
  const [on, cambiar] = useCheck(checked, defaultChecked, onCheckedChange)

  const control = (
    <>
      <button
        ref={ref}
        type="button"
        role="switch"
        aria-checked={on}
        disabled={disabled}
        className={cn("ysw-switch", size === "sm" && "ysw-switch--sm", className)}
        onClick={() => cambiar(!on)}
        {...props}
      />
      {/* Espejo oculto: conserva el `name` en un <form> nativo, como hacía Radix. */}
      {name ? (
        <input type="checkbox" name={name} checked={on} disabled={disabled} readOnly
               tabIndex={-1} aria-hidden="true"
               style={{ position: "absolute", width: 1, height: 1, opacity: 0, pointerEvents: "none", margin: 0 }} />
      ) : null}
    </>
  )

  if (!label && !hint && !error) return control

  const sangria = size === "sm" ? 48 : 54

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
        {control}
        {label && (
          <span style={{
            font: "500 14px/1.4 var(--font-sans)",
            color: disabled ? "var(--yool-muted, #8D8D8D)" : "var(--yool-black, #1A1A1A)",
            cursor: disabled ? "not-allowed" : "default",
          }}>
            {label}
          </span>
        )}
      </div>
      {hint && !error && (
        <p style={{ margin: `0 0 0 ${sangria}px`, font: "400 12px/1.5 var(--font-sans)", color: "var(--yool-gray1, #54585A)" }}>
          {hint}
        </p>
      )}
      {error && errorMessage && (
        <p style={{ margin: `0 0 0 ${sangria}px`, font: "400 12px/1.5 var(--font-sans)", color: "var(--yool-error, #ED4068)" }}>
          {errorMessage}
        </p>
      )}
    </div>
  )
})
Switch.displayName = "Switch"

/* ── RadioPills ─────────────────────────────────────────────────────────────
   Elección entre valores excluyentes cuando conviene tenerlos todos a la vista.
   `options` acepta `["a","b"]` o `[{ value, label, dot }]`.

   Las dos variantes del DS:
     RADIO PILL · SIN PUNTO        <RadioPills … />            (por defecto)
     RADIO PILL · PUNTO FANCYYOOL  <RadioPills dots … />       o `dot` por opción

   El punto conserva su color aun con el pill seleccionado en negro: ahí está lo
   distintivo de la variante, no es decoración que se apague.

   Emite el VALOR, no un evento, igual que `YoolRadioGroup` y `YoolTabs`. */
/* Los seis colores son los de `Y_RADIO_DOTS`, pero por TOKEN y no por hex.
   Es la única divergencia deliberada respecto del "YoolToggle (standalone)":
   ese archivo trae los hex pelados #6366F1, #EC4899 y #F59E0B, que son los
   Tailwind por defecto y están elegidos contra el fondo #1A1A1A de su página de
   muestra. Las pantallas de tadmin son claras, y `colors_and_type.css` define
   para eso `--fy-indigo #7D7CFF`, `--fy-pink #E91E63` y `--fy-orange #D35400`
   (anotado ahí mismo como "urgencia (light)"). Es además lo que ya usa el
   bundle que tadmin sirve en `public/ds/_ds_bundle.js`, así que el pill se ve
   igual con bundle y sin él. */
const PALETA_FANCY = [
  "var(--fy-cyan,#06B6D4)",
  "var(--fy-indigo,#7D7CFF)",
  "var(--fy-lime,#147D89)",
  "var(--fy-sky,#0EA5E9)",
  "var(--fy-pink,#E91E63)",
  "var(--fy-orange,#D35400)",
]

const RadioPills = React.forwardRef(({
  value,
  options = [],
  onChange,
  dots = false,
  disabled = false,
  className,
  "aria-label": ariaLabel = "Opciones",
  ...props
}, ref) => {
  ponerEstilos()
  return (
    <div ref={ref} className={cn("ysw-radio", className)} role="radiogroup" aria-label={ariaLabel} {...props}>
      {options.map((o, i) => {
        const esObj = o !== null && typeof o === "object"
        const val = esObj ? o.value : o
        const lbl = esObj ? o.label : o
        const dot = esObj && o.dot ? o.dot : dots ? PALETA_FANCY[i % PALETA_FANCY.length] : null
        const on = value === val
        return (
          <button
            key={val}
            type="button"
            role="radio"
            aria-checked={on}
            className="ysw-pill"
            disabled={disabled || (esObj && o.disabled)}
            onClick={() => onChange && onChange(val)}
          >
            {dot && <span className="ysw-pill__dot" style={{ background: dot }} />}
            {lbl}
          </button>
        )
      })}
    </div>
  )
})
RadioPills.displayName = "RadioPills"

export { Switch, RadioPills }
